import { useMemo } from "react";

import AttachedCloudIntegrationsSection from "components/AttachCloud/Details/CloudIntegrationsSection";
import DocumentationButton from "components/DocumentationButton";
import { EmptystateCloudColored } from "components/icons/generated";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import EmptyState from "ds/components/EmptyState";
import Feedback from "ds/components/Feedback";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageStack } from "hooks/useAnalytics/pages/stack";
import useTypedContext from "hooks/useTypedContext";
import { CLOUD_INTEGRATIONS, CloudIntegrationAttachment } from "shared/CloudIntegration/types";
import useAttachableCloudIntegrations from "shared/CloudIntegration/useAttachableCloudIntegrations";
import { isSelfHostedDistribution } from "utils/distribution";
import { getDocsUrl } from "utils/getDocsUrl";

import StackSettingsFormWrapper from "../../components/FormWrapper";
import { REFETCH_STACK_SETTINGS_QUERIES } from "../../constants";
import { StackSettingsContextData } from "../../Context";
import StackSettingsIntegrationsWrapper from "../components/Wrapper";
import { showStackSettingsIntegrationsCloudDrawer } from "./Drawer";
import styles from "./styles.module.css";

const isSelfHosted = isSelfHostedDistribution();

const StackSettingsIntegrationsCloud = () => {
  const { stackSettings, canManageStack } = useTypedContext(StackSettingsContextData);

  const trackSegmentEvent = useAnalytics({
    page: AnalyticsPageStack.StackSettingsIntegrations,
  });

  const {
    cloudConfig,
    attachableAzureIntegrations,
    attachableAwsIntegrations,
    hasData,
    refetch,
    refetching,
  } = useAttachableCloudIntegrations(stackSettings.spaceDetails.id);

  const onSuccessDetach = (type: CLOUD_INTEGRATIONS) => trackSegmentEvent("Detached", { type });

  const integrations = useMemo(() => {
    const awsIntegrations = stackSettings?.integrations?.awsV2 || [];
    const azureIntegrations = stackSettings?.integrations?.azure || [];

    return ([] as Array<CloudIntegrationAttachment>)
      .concat(awsIntegrations)
      .concat(azureIntegrations);
  }, [stackSettings.integrations?.awsV2, stackSettings.integrations?.azure]);

  const manuallyAttachedIntegrations = useMemo(() => {
    const manualIntegrations = integrations.filter((integration) => {
      if ("isAutoattached" in integration) {
        return !integration.isAutoattached;
      }
      return true; // if not autoattached, it's a manually attached integration
    });
    const gcpIntegration = stackSettings?.integrations?.gcp;

    if (gcpIntegration?.activated) {
      manualIntegrations.push(gcpIntegration);
    }

    return manualIntegrations;
  }, [integrations, stackSettings.integrations?.gcp]);

  const autoAttachedIntegrations = useMemo(() => {
    return integrations.filter((integration) => {
      if ("isAutoattached" in integration) {
        return integration.isAutoattached;
      }
      return false;
    });
  }, [integrations]);

  const gcpIntegration = stackSettings?.integrations?.gcp;

  const attachedIds = useMemo(
    () =>
      (integrations || [])
        // filter out GCP integration because it's handled separately in the attach drawer.
        .filter((integration) => "integrationId" in integration)
        .map((integration) => integration.integrationId),
    [integrations]
  );

  const filteredAwsIntegrations = useMemo(
    () => attachableAwsIntegrations.filter((integration) => !attachedIds.includes(integration.id)),
    [attachableAwsIntegrations, attachedIds]
  );

  const filteredAzureIntegrations = useMemo(
    () =>
      attachableAzureIntegrations.filter((integration) => !attachedIds.includes(integration.id)),
    [attachableAzureIntegrations, attachedIds]
  );

  const hasSomeIntegrationsAttached = integrations.length > 0 || gcpIntegration?.activated;
  const hasAttachableCloudIntegrations = hasData && cloudConfig.length > 0;

  const canAttachGcpIntegration = !gcpIntegration?.activated && !isSelfHosted;

  const canAttach =
    (filteredAwsIntegrations.length > 0 ||
      filteredAzureIntegrations.length > 0 ||
      canAttachGcpIntegration) &&
    canManageStack.result;

  const handleAttachIntegrationDrawer = () => {
    showStackSettingsIntegrationsCloudDrawer({
      stackId: stackSettings.id,
      hasData,
      refetch,
      refetching,
      cloudConfig,
      filteredAzureIntegrations,
      filteredAwsIntegrations,
      canAttachGcpIntegration,
    });
  };

  return (
    <StackSettingsIntegrationsWrapper
      actions={
        <Button variant="primary" disabled={!canAttach} onPress={handleAttachIntegrationDrawer}>
          Attach cloud integration
        </Button>
      }
    >
      <Feedback type="callout" variant="info">
        You can only attach integrations from the current space and parent spaces that you inherit
        from.
      </Feedback>
      {hasAttachableCloudIntegrations && !hasSomeIntegrationsAttached && (
        <Box align="center" justify="center" grow="1" fullWidth fullHeight>
          <EmptyState
            padding="large"
            icon={EmptystateCloudColored}
            title="You do not have any attached cloud integrations yet"
            caption="You can attach one of the existing ones, or create a new one."
          >
            <Box gap="medium">
              {canAttach && (
                <Button variant="primary" onPress={handleAttachIntegrationDrawer}>
                  Attach cloud integration
                </Button>
              )}
              <DocumentationButton
                to={getDocsUrl("/integrations/cloud-providers")}
                label="Documentation"
              />
            </Box>
          </EmptyState>
        </Box>
      )}

      {!hasAttachableCloudIntegrations && !hasSomeIntegrationsAttached && (
        <Box align="center" justify="center" grow="1" fullWidth fullHeight>
          <EmptyState
            padding="large"
            icon={EmptystateCloudColored}
            title="You do not have any cloud integrations yet"
            caption={
              <>
                Cloud Integrations are used to dynamically generate short-lived credentials to
                authenticate to cloud providers (AWS/Azure/GCP currently supported). In this way you
                avoid using static credentials and by doing so, you reduce breaches.
              </>
            }
          >
            <DocumentationButton
              to={getDocsUrl("/integrations/cloud-providers")}
              label="Documentation"
            />
          </EmptyState>
        </Box>
      )}

      {hasSomeIntegrationsAttached && (
        <Box
          direction="column"
          gap="x-large"
          fullWidth
          fullHeight
          className={styles.integrationsBackground}
        >
          <StackSettingsFormWrapper withLimitWidth={false} padding="0">
            <AttachedCloudIntegrationsSection
              title="Attached directly"
              integrations={manuallyAttachedIntegrations}
              cloudConfig={cloudConfig}
              stackId={stackSettings.id}
              readOnly={!canManageStack.result}
              onSuccessDetach={onSuccessDetach}
              stackLabels={stackSettings.labels}
              refetchQueries={REFETCH_STACK_SETTINGS_QUERIES}
            />
            <AttachedCloudIntegrationsSection
              title="Auto-attached"
              integrations={autoAttachedIntegrations}
              cloudConfig={cloudConfig}
              stackId={stackSettings.id}
              readOnly={true} // auto-attached integrations can't be detached by the user
              isAutoAttached={true}
              stackLabels={stackSettings.labels}
            />
          </StackSettingsFormWrapper>
        </Box>
      )}
    </StackSettingsIntegrationsWrapper>
  );
};

export default StackSettingsIntegrationsCloud;
